// src/pages/ResumeAnalysis.tsx
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import {
  analyticsOutline,
  briefcaseOutline,
  bulbOutline,
  codeSlashOutline,
  constructOutline,
  listOutline,
  personCircleOutline,
  schoolOutline,
  shieldCheckmarkOutline,
  gridOutline,
  cloudDownloadOutline,
  peopleOutline,
  shareOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { user as userApi } from '../services/api.ts';
import ShareModal from '../components/ShareModal.tsx';
import {
  ResumeAnalysisData,
  Experience,
  getContactInfo,
  getTechnicalSkills,
  getFormattedExperience,
  getFormattedEducation,
  getFormattedCertifications
} from '../utils/resumeAnalysisUtils.ts';

// Professional loading component
const ProfessionalAnalysisLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '50px',
    minHeight: '200px'
  }}>
    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: '3px',
            height: '24px',
            background: 'linear-gradient(to bottom, #4A6FA5, #2D4A6B)',
            borderRadius: '2px',
            animation: 'professionalLoading 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
    </div>
    <p style={{ color: 'var(--app-text-color-secondary)', fontSize: '0.9rem', margin: 0 }}>
      Loading analysis...
    </p>
  </div>
);

// --- Reusable UI Components ---
const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <IonCard style={{ marginTop: '20px', borderRadius: '12px', '--background': 'var(--app-card-background-color)', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}>
    <div style={{ padding: '16px', borderBottom: '1px solid var(--app-border-color)', display: 'flex', alignItems: 'center' }}>
      <IonIcon icon={icon} color="primary" style={{ fontSize: '20px', marginRight: '12px' }} />
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--app-text-color)', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ padding: '16px', color: 'var(--app-text-color-secondary)', lineHeight: 1.6 }}>
      {children}
    </div>
  </IonCard>
);

const ExperienceCard: React.FC<{ item: Experience }> = ({ item }) => (
  <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--app-border-color)', borderBottomStyle: 'dashed' }} className="last:border-b-0 last:pb-0 last:mb-0">
    <h4 style={{ fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px' }}>{item.title}</h4>
    <p style={{ color: 'var(--app-text-color)', fontWeight: '500', margin: 0 }}>{item.company}</p>
    <p style={{ fontSize: '0.875rem', color: 'var(--app-text-color-secondary)', margin: '4px 0 0' }}>{item.start_date} - {item.end_date}</p>
    <p style={{ fontSize: '1rem', margin: '12px 0' }}>{item.description}</p>
    {item.key_achievements && item.key_achievements.length > 0 && (
      <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
        {item.key_achievements.map((achievement, index) => (
          <li key={index} style={{ marginBottom: '4px', fontSize: '0.95rem' }}>{achievement}</li>
        ))}
      </ul>
    )}
  </div>
);


const ResumeAnalysis: React.FC = () => {
  const { resumeId } = useParams<{ resumeId: string }>();
  const [analysisData, setAnalysisData] = useState<ResumeAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [present] = useIonToast();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [downloading, setDownloading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Track window resize for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive container styles based on screen size
  const getContainerStyle = (): React.CSSProperties => {
    if (windowWidth < 768) {
      // Mobile: full width with padding
      return {
        maxWidth: '100%',
        margin: '0 auto',
        padding: '0 16px',
      };
    } else if (windowWidth < 1024) {
      // Tablet: 90% width
      return {
        maxWidth: '90%',
        margin: '0 auto',
        padding: '0 20px',
      };
    } else {
      // Desktop: 70% width
      return {
        maxWidth: '70%',
        margin: '0 auto',
        padding: '0 20px',
      };
    }
  };

  const containerStyle = getContainerStyle();

  // View mode state: traditional (current) or tabbed (new)
  const [viewMode, setViewMode] = useState<'traditional' | 'tabbed'>(() => {
    return (localStorage.getItem('resumeAnalysisView') as 'traditional' | 'tabbed') || 'tabbed'; // Default to tabbed
  });

  // Active tab for tabbed view
  const [activeTab, setActiveTab] = useState('overview');

  // Save view preference
  const toggleViewMode = () => {
    const newMode = viewMode === 'traditional' ? 'tabbed' : 'traditional';
    setViewMode(newMode);
    localStorage.setItem('resumeAnalysisView', newMode);
  };

  // Add CSS animations for professional loading
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes professionalLoading {
        0%, 40%, 100% { transform: scaleY(0.4); opacity: 0.5; }
        20% { transform: scaleY(1.0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!resumeId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await userApi.getResumeAnalysis(resumeId);
        if (data.status === 'completed' && data.analysis) {
          setAnalysisData(data.analysis);
        } else if (data.status) {
          setError(`This resume's analysis is currently in '${data.status}' status.`);
        } else {
          setAnalysisData(data);
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || 'Failed to load resume analysis.';
        setError(errorMessage);
        present({ message: errorMessage, duration: 3000, color: 'danger' });
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [resumeId, present]);

  // Handle download resume
  const handleDownloadResume = async () => {
    if (!resumeId) return;
    setDownloading(true);
    try {
      const { blob, filename } = await userApi.downloadOriginalResume(resumeId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      present({ message: 'Resume downloaded successfully!', duration: 2000, color: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to download resume.';
      present({ message: errorMessage, duration: 3000, color: 'danger' });
    } finally {
      setDownloading(false);
    }
  };

  // Handle coming soon action
  const handleComingSoon = () => {
    present({
      message: 'Referral requests are coming soon! Stay tuned for this exciting feature.',
      duration: 3000,
      color: 'primary'
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <div className="resume-analysis-container" style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 16px',
            width: '100%'
          }}>
            <ProfessionalAnalysisLoader />
          </div>
          <style>{`
            @media (max-width: 768px) {
              .resume-analysis-container {
                max-width: 100% !important;
                padding: 0 12px !important;
              }
            }
          `}</style>
        </>
      );
    }
    if (error) {
      return (
        <div className="resume-analysis-container" style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 16px',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', paddingTop: '50px', color: 'var(--ion-color-danger)' }}>
            <p>{error}</p>
          </div>
        </div>
      );
    }
    if (!analysisData) {
      return (
        <div className="resume-analysis-container" style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 16px',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', paddingTop: '50px' }}>
            <p>No analysis data found.</p>
          </div>
        </div>
      );
    }

    const contactInfo = getContactInfo(analysisData);
    const technicalSkills = getTechnicalSkills(analysisData);
    const experience = getFormattedExperience(analysisData);
    const education = getFormattedEducation(analysisData);
    const certifications = getFormattedCertifications(analysisData);

    // Traditional view (current implementation)
    const renderTraditionalView = () => (
      <div className="resume-analysis-container" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 16px',
        width: '100%'
      }}>
        <IonCard style={{ '--background': 'var(--app-card-background-color)', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', padding: '24px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--app-text-color)', margin: '0 0 8px' }}>{contactInfo.name}</h1>

          {/* Contact Information */}
          {contactInfo.location && (
            <p style={{ color: 'var(--app-text-color)', margin: '4px 0', fontSize: windowWidth < 768 ? '0.95rem' : '1.1rem' }}>
              {contactInfo.location}
            </p>
          )}

          <div style={{ margin: '8px 0', fontSize: '1rem', color: 'var(--app-text-color)' }}>
            {contactInfo.phone && (
              <span style={{ display: windowWidth < 768 ? 'block' : 'inline', marginBottom: windowWidth < 768 ? '4px' : '0' }}>Phone: {contactInfo.phone}</span>
            )}
            {contactInfo.phone && contactInfo.email && windowWidth >= 768 && <span> • </span>}
            {contactInfo.email && (
              <span style={{ display: windowWidth < 768 ? 'block' : 'inline' }}>E-Mail: <a href={`mailto:${contactInfo.email}`} style={{ color: 'var(--ion-color-primary)', textDecoration: 'none', wordBreak: 'break-word' }}>{contactInfo.email}</a></span>
            )}
          </div>

        </IonCard>

        <SectionCard title="Professional Summary" icon={analyticsOutline}>
          <p style={{ fontStyle: 'italic', fontSize: '1.05rem' }}>{analysisData.summary}</p>
        </SectionCard>

        <SectionCard title="Hard Skills" icon={codeSlashOutline}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {technicalSkills.map(skill => <IonChip key={skill.name}>{skill.name}</IonChip>)}
          </div>
        </SectionCard>

        <SectionCard title="Experience" icon={briefcaseOutline}>
          {experience.map((item, index) => <ExperienceCard key={index} item={item} />)}
        </SectionCard>

        <SectionCard title="Education" icon={schoolOutline}>
          {education.map((item, index) => (
            <div key={index} style={{ marginBottom: '16px' }} className="last:mb-0">
              <h4 style={{ fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px' }}>{item.degree}, {item.field}</h4>
              <p style={{ color: 'var(--app-text-color-secondary)', margin: 0 }}>
                {item.institution}
                {item.graduation_date && item.graduation_date.toLowerCase() !== 'unknown' && ` · ${item.graduation_date}`}
              </p>
            </div>
          ))}
        </SectionCard>
        <SectionCard title="Certifications" icon={shieldCheckmarkOutline}>
          <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
            {certifications.map(cert => <li key={cert.name} style={{ marginBottom: '8px' }}>{cert.name} - {cert.issuer} ({cert.date})</li>)}
          </ul>
        </SectionCard>
      </div>
    );

    // Tabbed view (new implementation)
    const renderTabbedView = () => (
      <div className="resume-analysis-container" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 16px',
        width: '100%'
      }}>
        {/* Tab Navigation */}
        <IonSegment
          value={activeTab}
          onIonChange={(e) => setActiveTab(e.detail.value as string)}
          className="resume-tabs-segment"
          style={{ marginBottom: '20px' }}
        >
          <IonSegmentButton value="overview" className="resume-tab-button">
            <IonIcon icon={personCircleOutline} className="tab-icon" />
            <IonLabel className="tab-label">Overview</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="experience" className="resume-tab-button">
            <IonIcon icon={briefcaseOutline} className="tab-icon" />
            <IonLabel className="tab-label">Experience</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="skills" className="resume-tab-button">
            <IonIcon icon={codeSlashOutline} className="tab-icon" />
            <IonLabel className="tab-label">Skills</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="education" className="resume-tab-button">
            <IonIcon icon={schoolOutline} className="tab-icon" />
            <IonLabel className="tab-label">Education</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {/* Improved mobile tab styling */}
        <style>{`
          .resume-tabs-segment {
            background: var(--ion-color-light);
            border-radius: 8px;
            padding: 4px;
          }
          
          .resume-tab-button {
            font-size: 14px;
            font-weight: 500;
            min-height: 40px;
          }
          
          .tab-icon {
            display: none;
            font-size: 18px;
            margin-bottom: 2px;
          }
          
          /* Mobile optimizations */
          @media (max-width: 768px) {
            .resume-tabs-segment {
              padding: 2px;
            }
            
            .resume-tab-button {
              font-size: 13px;
              min-width: 0;
              flex: 1;
              padding: 8px 4px;
            }
            
            /* Show icons on very small screens */
            @media (max-width: 400px) {
              .tab-icon {
                display: block;
              }
              
              .tab-label {
                font-size: 11px;
                margin-top: 2px;
              }
              
              .resume-tab-button {
                flex-direction: column;
                padding: 6px 2px;
              }
            }
          }
        `}</style>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            <IonCard style={{ '--background': 'var(--app-card-background-color)', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', padding: '24px', textAlign: 'center' }}>
              <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--app-text-color)', margin: '0 0 8px' }}>{contactInfo.name}</h1>
              {contactInfo.location && (
                <p style={{ color: 'var(--app-text-color)', margin: '4px 0', fontSize: '1.1rem' }}>
                  {contactInfo.location}
                </p>
              )}
              <div style={{ margin: '8px 0', fontSize: '1rem', color: 'var(--app-text-color)' }}>
                {contactInfo.phone && <span>Phone: {contactInfo.phone}</span>}
                {contactInfo.phone && contactInfo.email && <span> • </span>}
                {contactInfo.email && (
                  <span>E-Mail: <a href={`mailto:${contactInfo.email}`} style={{ color: 'var(--ion-color-primary)', textDecoration: 'none' }}>{contactInfo.email}</a></span>
                )}
              </div>
            </IonCard>

            <SectionCard title="Professional Summary" icon={analyticsOutline}>
              <p style={{ fontStyle: 'italic', fontSize: '1.05rem' }}>{analysisData.summary}</p>
            </SectionCard>

            {/* Quick Stats */}
            <IonCard style={{ marginTop: '20px', borderRadius: '12px', '--background': 'var(--app-card-background-color)' }}>
              <div style={{ padding: '16px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 16px' }}>Quick Stats</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: windowWidth < 480 ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: windowWidth < 480 ? '12px' : '16px'
                }}>
                  <div style={{ textAlign: 'center', padding: windowWidth < 480 ? '8px 4px' : '0' }}>
                    <div style={{
                      fontSize: windowWidth < 480 ? '1.5rem' : '2rem',
                      fontWeight: 'bold',
                      color: 'var(--ion-color-primary)'
                    }}>
                      {experience.length}
                    </div>
                    <div style={{
                      fontSize: windowWidth < 480 ? '0.75rem' : '0.9rem',
                      color: 'var(--app-text-color-secondary)',
                      marginTop: '4px'
                    }}>
                      Positions
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: windowWidth < 480 ? '8px 4px' : '0' }}>
                    <div style={{
                      fontSize: windowWidth < 480 ? '1.5rem' : '2rem',
                      fontWeight: 'bold',
                      color: 'var(--ion-color-primary)'
                    }}>
                      {technicalSkills.length}
                    </div>
                    <div style={{
                      fontSize: windowWidth < 480 ? '0.75rem' : '0.9rem',
                      color: 'var(--app-text-color-secondary)',
                      marginTop: '4px'
                    }}>
                      Skills
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: windowWidth < 480 ? '8px 4px' : '0' }}>
                    <div style={{
                      fontSize: windowWidth < 480 ? '1.5rem' : '2rem',
                      fontWeight: 'bold',
                      color: 'var(--ion-color-primary)'
                    }}>
                      {education.length}
                    </div>
                    <div style={{
                      fontSize: windowWidth < 480 ? '0.75rem' : '0.9rem',
                      color: 'var(--app-text-color-secondary)',
                      marginTop: '4px'
                    }}>
                      Degrees
                    </div>
                  </div>
                </div>
              </div>
            </IonCard>
          </>
        )}

        {activeTab === 'experience' && (
          <SectionCard title="Work Experience" icon={briefcaseOutline}>
            {experience.map((item, index) => <ExperienceCard key={index} item={item} />)}
          </SectionCard>
        )}

        {activeTab === 'skills' && (
          <SectionCard title="Technical Skills" icon={codeSlashOutline}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {technicalSkills.map(skill => (
                <IonChip key={skill.name} color="primary" style={{ fontWeight: '500' }}>
                  {skill.name}
                </IonChip>
              ))}
            </div>
          </SectionCard>
        )}

        {activeTab === 'education' && (
          <>
            <SectionCard title="Education" icon={schoolOutline}>
              {education.map((item, index) => (
                <div key={index} style={{ marginBottom: '16px' }} className="last:mb-0">
                  <h4 style={{ fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px' }}>{item.degree}, {item.field}</h4>
                  <p style={{ color: 'var(--app-text-color-secondary)', margin: 0 }}>
                    {item.institution}
                    {item.graduation_date && item.graduation_date.toLowerCase() !== 'unknown' && ` · ${item.graduation_date}`}
                  </p>
                </div>
              ))}
            </SectionCard>

            {certifications.length > 0 && (
              <SectionCard title="Certifications" icon={shieldCheckmarkOutline}>
                <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                  {certifications.map(cert => (
                    <li key={cert.name} style={{ marginBottom: '8px' }}>
                      {cert.name} - {cert.issuer} ({cert.date})
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </>
        )}
      </div>
    );

    return viewMode === 'traditional' ? renderTraditionalView() : renderTabbedView();
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar style={{ '--background': 'var(--app-background-color)' }}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/profile" color="primary" />
          </IonButtons>
          <IonTitle style={{ fontWeight: 700, color: 'var(--app-text-color)' }}>Resume Analysis</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={toggleViewMode} title={`Switch to ${viewMode === 'traditional' ? 'tabbed' : 'traditional'} view`}>
              <IonIcon icon={viewMode === 'traditional' ? gridOutline : listOutline} style={{ fontSize: '24px' }} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': 'var(--app-background-color)', '--padding-start': windowWidth < 768 ? '0px' : '16px', '--padding-end': windowWidth < 768 ? '0px' : '16px', '--padding-top': '16px', '--padding-bottom': windowWidth < 480 ? '200px' : windowWidth < 768 ? '140px' : '120px' }}>
        {renderContent()}

        {/* Sticky CTA Footer - Only show when content is loaded */}
        {!loading && !error && analysisData && (
          <div className="sticky-cta-footer" style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'var(--app-card-background-color)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
            padding: windowWidth < 768 ? '12px 16px' : '16px 24px',
            zIndex: 1000,
            animation: 'slideUpFade 0.4s ease-out'
          }}>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              gap: windowWidth < 768 ? '8px' : '12px',
              flexDirection: windowWidth < 480 ? 'column' : 'row',
              alignItems: 'stretch'
            }}>
              {/* Primary Action: Download Resume */}
              <IonButton
                expand="block"
                color="primary"
                onClick={handleDownloadResume}
                disabled={downloading}
                style={{
                  flex: 1,
                  '--border-radius': '12px',
                  height: '48px',
                  fontSize: windowWidth < 768 ? '14px' : '15px',
                  fontWeight: '600',
                  textTransform: 'none',
                  margin: 0
                }}
              >
                <IonIcon icon={cloudDownloadOutline} slot="start" />
                {downloading ? 'Downloading...' : 'Download'}
              </IonButton>

              {/* Share Resume */}
              <IonButton
                expand="block"
                fill="outline"
                color="primary"
                onClick={() => setShowShareModal(true)}
                style={{
                  flex: 1,
                  '--border-radius': '12px',
                  height: '48px',
                  fontSize: windowWidth < 768 ? '14px' : '15px',
                  fontWeight: '600',
                  textTransform: 'none',
                  margin: 0
                }}
              >
                <IonIcon icon={shareOutline} slot="start" />
                Share
              </IonButton>

              {/* Secondary Action: Request Referral (Coming Soon) */}
              <IonButton
                expand="block"
                fill="outline"
                color="primary"
                onClick={handleComingSoon}
                style={{
                  flex: 1,
                  '--border-radius': '12px',
                  height: '48px',
                  fontSize: windowWidth < 768 ? '14px' : '15px',
                  fontWeight: '600',
                  textTransform: 'none',
                  margin: 0,
                  position: 'relative'
                }}
              >
                <IonIcon icon={peopleOutline} slot="start" />
                {windowWidth < 768 ? 'Referral' : 'Request Referral'}
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#FF6B6B',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  textTransform: 'uppercase'
                }}>
                  Soon
                </span>
              </IonButton>
            </div>
          </div>
        )}

        {/* Add animation styles */}
        <style>{`
          @keyframes slideUpFade {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          /* Ensure content doesn't get hidden behind sticky footer */
          .resume-analysis-container {
            padding-bottom: 40px;
          }

          @media (max-width: 480px) {
            .resume-analysis-container {
              padding-bottom: 60px;
            }
          }

          /* Smooth footer appearance */
          .sticky-cta-footer {
            transition: transform 0.3s ease, opacity 0.3s ease;
          }
        `}</style>
      </IonContent>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resumeId={resumeId || null}
      />
    </IonPage>
  );
};

export default ResumeAnalysis;